/**
 * 打版触发规则(release-trigger-rule)配置页面。
 *
 * 挂在 JobList 顶部 Tab, 与「Job 列表」「定时列表」并列。上下两块布局:
 *   - 上:规则表(增删改查/启停/试正则)
 *   - 下:最近打版事件(release_build)分页
 *
 * 权限点 release_trigger_rule:manage —— 页面/按钮统一通过 useCurrentUser().hasPerm 隐藏。
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  App, Button, Drawer, Form, Input, Modal, Popconfirm, Space, Switch, Table, Tag, Tooltip,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  createReleaseTriggerRule,
  deleteReleaseTriggerRule,
  getReleaseTriggerJobParamsTemplate,
  listReleaseBuilds,
  listReleaseTriggerRules,
  testReleaseTriggerRules,
  updateReleaseTriggerRule,
  type CreateReleaseTriggerRuleRequest,
  type ReleaseBuildVo,
  type ReleaseTriggerRuleVo,
} from '@apis/simulation';
import { useCurrentUser } from '@/common/auth/adminAcl';
import CreateJobModal from '@components/CreateJobModal';

const PERM = 'release_trigger_rule:manage';

interface RuleFormValues {
  name: string;
  versionRegex: string;
  jobParamsJson: string;   // Antd 表单里存原文, submit 时 JSON.parse
  description?: string;
  enabled: boolean;
}

const _parseJobParamsSafe = (raw: string): { ok: true; value: Record<string, unknown> } | { ok: false; error: string } => {
  try {
    const value = JSON.parse(raw);
    if (typeof value !== 'object' || value === null || Array.isArray(value)) {
      return { ok: false, error: 'jobParams 必须是 JSON 对象' };
    }
    return { ok: true, value };
  } catch (e) {
    return { ok: false, error: `JSON 解析失败: ${(e as Error).message}` };
  }
};

const _stringifyJobParams = (obj: Record<string, unknown>): string => {
  try {
    return JSON.stringify(obj, null, 2);
  } catch {
    return '{}';
  }
};

export default function ReleaseTriggerTab() {
  const { message } = App.useApp();
  const { hasPerm } = useCurrentUser();
  const canManage = hasPerm(PERM);

  // 上半屏:规则列表
  const [rules, setRules] = useState<ReleaseTriggerRuleVo[]>([]);
  const [rulesLoading, setRulesLoading] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<ReleaseTriggerRuleVo | null>(null);
  const [form] = Form.useForm<RuleFormValues>();

  // 试正则弹窗
  const [testOpen, setTestOpen] = useState(false);
  const [testVersion, setTestVersion] = useState('');
  const [testHits, setTestHits] = useState<ReleaseTriggerRuleVo[] | null>(null);
  const [testLoading, setTestLoading] = useState(false);

  // 下半屏:最近打版事件分页
  const [builds, setBuilds] = useState<ReleaseBuildVo[]>([]);
  const [buildsLoading, setBuildsLoading] = useState(false);
  const [buildsPage, setBuildsPage] = useState(1);
  const [buildsSize, setBuildsSize] = useState(20);
  const [buildsTotal, setBuildsTotal] = useState(0);
  const [rawViewer, setRawViewer] = useState<ReleaseBuildVo | null>(null);
  const [jobBuilderOpen, setJobBuilderOpen] = useState(false);

  const loadRules = useCallback(async () => {
    setRulesLoading(true);
    try {
      const list = await listReleaseTriggerRules();
      setRules(list);
    } catch (e) {
      console.error('加载规则失败:', e);
      message.error(`加载规则失败: ${(e as Error).message}`);
    } finally {
      setRulesLoading(false);
    }
  }, [message]);

  const loadBuilds = useCallback(async (page: number, pageSize: number) => {
    setBuildsLoading(true);
    try {
      const res = await listReleaseBuilds({ current: page, pageSize });
      setBuilds(res.items);
      setBuildsTotal(res.total);
      setBuildsPage(res.current);
      setBuildsSize(res.pageSize);
    } catch (e) {
      console.error('加载最近打版事件失败:', e);
      message.error(`加载最近打版失败: ${(e as Error).message}`);
    } finally {
      setBuildsLoading(false);
    }
  }, [message]);

  useEffect(() => {
    if (!canManage) return;
    loadRules();
    loadBuilds(1, buildsSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canManage]);

  const openCreate = async () => {
    setEditing(null);
    // 从后端拿默认 Job 参数(sceneSetIds 走 yaml neolix.daily.scene-set-ids,
    // test / prod 值不同);拿不到就退化到硬编默认,不阻塞打开。
    let template: Record<string, unknown> = {
      taskTypeId: 1,
      sceneSetIds: '',
      priority: 6,
      jobNameTemplate: 'rb-{version}-{build_id}',
    };
    try {
      const t = await getReleaseTriggerJobParamsTemplate();
      if (t && Object.keys(t).length > 0) template = t;
    } catch {
      // 静默兜底,用上面的 fallback
    }
    form.setFieldsValue({
      name: '',
      versionRegex: '',
      jobParamsJson: JSON.stringify(template, null, 2),
      description: '',
      enabled: true,
    });
    setEditorOpen(true);
  };

  const openEdit = (row: ReleaseTriggerRuleVo) => {
    setEditing(row);
    form.setFieldsValue({
      name: row.name,
      versionRegex: row.versionRegex,
      jobParamsJson: _stringifyJobParams(row.jobParams),
      description: row.description || '',
      enabled: row.enabled,
    });
    setEditorOpen(true);
  };

  const handleSubmit = async () => {
    let values: RuleFormValues;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    const parsed = _parseJobParamsSafe(values.jobParamsJson);
    if (!parsed.ok) {
      message.error(parsed.error);
      return;
    }
    const payload: CreateReleaseTriggerRuleRequest = {
      name: values.name.trim(),
      versionRegex: values.versionRegex,
      jobParams: parsed.value,
      enabled: values.enabled,
      description: values.description?.trim() || null,
    };
    try {
      if (editing) {
        await updateReleaseTriggerRule(editing.id, payload);
        message.success('规则更新成功');
      } else {
        await createReleaseTriggerRule(payload);
        message.success('规则创建成功');
      }
      setEditorOpen(false);
      loadRules();
    } catch (e) {
      message.error(`保存失败: ${(e as Error).message}`);
    }
  };

  const handleDelete = async (row: ReleaseTriggerRuleVo) => {
    try {
      await deleteReleaseTriggerRule(row.id);
      message.success('规则已删除');
      loadRules();
    } catch (e) {
      message.error(`删除失败: ${(e as Error).message}`);
    }
  };

  const handleToggleEnabled = async (row: ReleaseTriggerRuleVo, next: boolean) => {
    try {
      await updateReleaseTriggerRule(row.id, { enabled: next });
      setRules((prev) => prev.map((r) => (r.id === row.id ? { ...r, enabled: next } : r)));
    } catch (e) {
      message.error(`切换启用失败: ${(e as Error).message}`);
    }
  };

  const handleTest = async () => {
    if (!testVersion.trim()) {
      message.warning('请输入待试版本号');
      return;
    }
    setTestLoading(true);
    try {
      const hits = await testReleaseTriggerRules(testVersion.trim());
      setTestHits(hits);
    } catch (e) {
      message.error(`试匹配失败: ${(e as Error).message}`);
    } finally {
      setTestLoading(false);
    }
  };

  const rulesColumns: ColumnsType<ReleaseTriggerRuleVo> = useMemo(() => [
    { title: 'ID', dataIndex: 'id', width: 70 },
    { title: '规则名', dataIndex: 'name', width: 200, ellipsis: true },
    {
      title: 'version 正则', dataIndex: 'versionRegex', ellipsis: true,
      render: (v: string) => <Tooltip title={v}><code>{v}</code></Tooltip>,
    },
    {
      title: 'Job 参数', dataIndex: 'jobParams', width: 260, ellipsis: true,
      render: (p: Record<string, unknown>) => (
        <Tooltip title={<pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{_stringifyJobParams(p)}</pre>}>
          <span style={{ color: '#5e6ad2' }}>
            {Object.keys(p || {}).length} 个字段
          </span>
        </Tooltip>
      ),
    },
    {
      title: '启用', dataIndex: 'enabled', width: 90,
      render: (v: boolean, row) => (
        <Switch
          checked={v}
          disabled={!canManage}
          onChange={(next) => handleToggleEnabled(row, next)}
          size="small"
        />
      ),
    },
    { title: '创建人', dataIndex: 'creator', width: 130, ellipsis: true },
    { title: '更新时间', dataIndex: 'updatedAt', width: 170 },
    {
      title: '操作', width: 160, fixed: 'right',
      render: (_v, row) => (
        <Space size="small">
          <Button size="small" type="link" disabled={!canManage} onClick={() => openEdit(row)}>编辑</Button>
          <Popconfirm
            title="删除后不可恢复,确认删除?"
            onConfirm={() => handleDelete(row)}
            okText="删除" cancelText="取消"
            disabled={!canManage}
          >
            <Button size="small" type="link" danger disabled={!canManage}>删除</Button>
          </Popconfirm>
        </Space>
      ),
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [canManage]);

  const buildsColumns: ColumnsType<ReleaseBuildVo> = useMemo(() => [
    { title: 'ID', dataIndex: 'id', width: 70 },
    { title: 'version', dataIndex: 'version', width: 220, ellipsis: true },
    {
      title: 'build_id', dataIndex: 'buildId', width: 220, ellipsis: true,
      render: (v: string) => <code>{v}</code>,
    },
    {
      title: 'bosPath', width: 260, ellipsis: true,
      render: (_v, row) => {
        const pkg = (row.rawEvent?.package || {}) as Record<string, unknown>;
        const p = (pkg.bosPath ?? '') as string;
        return <Tooltip title={p}><span>{p || '—'}</span></Tooltip>;
      },
    },
    { title: '接收时间', dataIndex: 'receivedAt', width: 180 },
    {
      title: '原始事件', width: 100, fixed: 'right',
      render: (_v, row) => (
        <Button size="small" type="link" onClick={() => setRawViewer(row)}>查看</Button>
      ),
    },
  ], []);

  if (!canManage) {
    return (
      <div style={{ padding: 24, color: '#8c8c8c' }}>
        无权限访问打版触发,请联系管理员添加权限。
      </div>
    );
  }

  return (
    <div className="layout-content">
      {/* 上半屏:规则表 */}
      <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
        <h3 style={{ margin: 0, flex: 'none' }}>触发规则</h3>
        <span style={{ color: '#8c8c8c', fontSize: 12 }}>
          匹配 Kafka 打版事件的 version 后自动建 Job(全部命中都建)
        </span>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <Button onClick={() => setTestOpen(true)}>试正则</Button>
          <Button onClick={loadRules}>刷新</Button>
          <Button type="primary" onClick={openCreate}>新建规则</Button>
        </div>
      </div>
      <Table
        rowKey="id"
        dataSource={rules}
        columns={rulesColumns}
        loading={rulesLoading}
        size="small"
        scroll={{ x: 'max-content' }}
        pagination={false}
      />

      {/* 下半屏:最近打版事件 */}
      <div style={{ marginTop: 24, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
        <h3 style={{ margin: 0, flex: 'none' }}>最近打版事件</h3>
        <span style={{ color: '#8c8c8c', fontSize: 12 }}>
          消息落库流水
        </span>
        <div style={{ marginLeft: 'auto' }}>
          <Button onClick={() => loadBuilds(buildsPage, buildsSize)}>刷新</Button>
        </div>
      </div>
      <Table
        rowKey="id"
        dataSource={builds}
        columns={buildsColumns}
        loading={buildsLoading}
        size="small"
        scroll={{ x: 'max-content' }}
        pagination={{
          current: buildsPage,
          pageSize: buildsSize,
          total: buildsTotal,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50', '100'],
          onChange: (p, s) => loadBuilds(p, s),
        }}
      />

      {/* 新建/编辑抽屉 */}
      <Drawer
        title={editing ? `编辑规则 #${editing.id}` : '新建规则'}
        width={640}
        open={editorOpen}
        onClose={() => setEditorOpen(false)}
        extra={
          <Space>
            <Button onClick={() => setEditorOpen(false)}>取消</Button>
            <Button type="primary" onClick={handleSubmit}>保存</Button>
          </Space>
        }
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="name" label="规则名"
            rules={[{ required: true, message: '必填' }, { max: 128 }]}
          >
            <Input placeholder="例:x86 主线 daily 走全量" />
          </Form.Item>
          <Form.Item
            name="versionRegex" label="version 正则(Python re 语法)"
            rules={[{ required: true, message: '必填' }, { max: 512 }]}
            extra="示例: ^release\\.10\\.10\\.\\d+$(整串匹配) / release\\.10\\.10 (部分匹配, re.search)"
          >
            <Input placeholder="^release\.10\.10\.\d+$" />
          </Form.Item>
          <Form.Item
            name="jobParamsJson" label="Job 参数(JSON, 对应 JobCreateRequest / BazelJobCreateRequest)"
            rules={[{ required: true, message: '必填' }]}
            extra={(
              <>
                jobNameTemplate 支持占位符 {'{version}'} / {'{build_id}'};buildSpec.algorithmVersion / baseVersion 里的
                {' '}{'{version}'} 会在消费打版事件时替换为实际版本号。
                <br />
                <Button
                  type="link" size="small" style={{ padding: 0 }}
                  onClick={() => setJobBuilderOpen(true)}
                >
                  用「新建 Job」弹窗快捷生成 →
                </Button>
              </>
            )}
          >
            <Input.TextArea rows={12} spellCheck={false} style={{ fontFamily: 'monospace' }} />
          </Form.Item>
          <Form.Item name="description" label="说明(可空)">
            <Input.TextArea rows={2} maxLength={512} />
          </Form.Item>
          <Form.Item name="enabled" label="启用" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Drawer>

      {/* 试正则弹窗 */}
      <Modal
        title="试正则"
        open={testOpen}
        onCancel={() => { setTestOpen(false); setTestHits(null); }}
        footer={<Button onClick={() => { setTestOpen(false); setTestHits(null); }}>关闭</Button>}
        width={640}
      >
        <Space.Compact style={{ width: '100%', marginBottom: 12 }}>
          <Input
            placeholder="待试版本号,如 release.10.10.5"
            value={testVersion}
            onChange={(e) => setTestVersion(e.target.value)}
            onPressEnter={handleTest}
          />
          <Button type="primary" onClick={handleTest} loading={testLoading}>匹配</Button>
        </Space.Compact>
        {testHits === null ? (
          <div style={{ color: '#8c8c8c' }}>输入版本号,点击「匹配」查看命中的启用规则</div>
        ) : testHits.length === 0 ? (
          <div style={{ color: '#faad14' }}>该 version 无命中(仅匹配 enabled=true 的规则)</div>
        ) : (
          <div>
            <div style={{ marginBottom: 8, color: '#8c8c8c' }}>
              命中 {testHits.length} 条规则:
            </div>
            {testHits.map((r) => (
              <div key={r.id} style={{ padding: 8, marginBottom: 6, background: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: 4 }}>
                <div><Tag color="green">#{r.id}</Tag> {r.name}</div>
                <div><code style={{ fontSize: 12 }}>{r.versionRegex}</code></div>
              </div>
            ))}
          </div>
        )}
      </Modal>

      {/* 原始事件 JSON 查看 */}
      <Modal
        title={rawViewer ? `原始事件 #${rawViewer.id} · ${rawViewer.buildId}` : '原始事件'}
        open={rawViewer !== null}
        onCancel={() => setRawViewer(null)}
        footer={<Button onClick={() => setRawViewer(null)}>关闭</Button>}
        width={720}
      >
        {rawViewer && (
          <pre style={{
            maxHeight: 480, overflow: 'auto', background: '#fafafa',
            padding: 12, borderRadius: 4, margin: 0,
          }}>
            {JSON.stringify(rawViewer.rawEvent, null, 2)}
          </pre>
        )}
      </Modal>

      {/* 快捷生成 Job 参数 JSON:复用「新建 Job」弹窗,dry-run 模式不真调 API,
          回填时把 buildSpec.algorithmVersion (按包) / baseVersion (按 bazel) 换成
          {version} 占位符,消费打版事件时后端会替换成真实版本号。 */}
      <CreateJobModal
        open={jobBuilderOpen}
        onClose={() => setJobBuilderOpen(false)}
        dryRunMode
        onDryRunResult={({ endpoint, params }) => {
          form.setFieldsValue({
            // endpoint 字段决定后端 dispatch 路径(create / bazel_create);
            // jobNameTemplate 让用户按需改写,默认 rb-{version}-{build_id}。
            jobParamsJson: JSON.stringify(
              {
                endpoint,
                ...params,
                jobNameTemplate:
                  (form.getFieldValue('jobParamsJson')
                    && (() => {
                      try {
                        const cur = JSON.parse(form.getFieldValue('jobParamsJson'));
                        return cur.jobNameTemplate;
                      } catch {
                        return undefined;
                      }
                    })()) || 'rb-{version}-{build_id}',
              },
              null,
              2,
            ),
          });
        }}
      />
    </div>
  );
}
